/**
 * INP Fix #1 — Defer Third-Party Scripts & Break Up Long Tasks
 * Site: parasgalleryfabrics.com
 *
 * HOW TO USE:
 *   Add this as the LAST script in your <body>, or load it with defer:
 *   <script src="inp-fix-1-defer-and-longtasks.js" defer></script>
 *
 *   Then REMOVE the original <script> tags for GTM, analytics, chat widgets, etc.
 *   and let this file load them after the page is interactive.
 */

// ─────────────────────────────────────────────────────────────
// 1. YIELD HELPER — breaks up long tasks so the browser can
//    respond to user input between chunks of work.
//    Use this anywhere you have loops or heavy processing.
// ─────────────────────────────────────────────────────────────

/**
 * Yields control back to the browser main thread.
 * Allows pending input events (clicks, taps) to be handled
 * before the next chunk of work runs — directly lowers INP.
 *
 * Usage:
 *   for (const item of bigArray) {
 *     processItem(item);
 *     await yieldToMain();   // ← insert this inside loops
 *   }
 */
function yieldToMain() {
  // Use the Scheduler API if available (Chrome 115+), else fall back to setTimeout
  if ('scheduler' in window && 'yield' in scheduler) {
    return scheduler.yield();
  }
  return new Promise(resolve => setTimeout(resolve, 0));
}

// ─────────────────────────────────────────────────────────────
// 2. CHUNK PROCESSOR — run an array of tasks in small batches,
//    yielding between each batch so INP stays low.
// ─────────────────────────────────────────────────────────────

/**
 * @param {Array}    items      - Array of items to process
 * @param {Function} processFn  - Called with each item
 * @param {number}   chunkSize  - Items per chunk (default: 5)
 */
async function processInChunks(items, processFn, chunkSize = 5) {
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    chunk.forEach(processFn);
    await yieldToMain(); // yield after every chunk
  }
}

// ─────────────────────────────────────────────────────────────
// 3. DEFERRED SCRIPT LOADER
//    Loads third-party scripts only after the page is idle
//    and the user has had a chance to interact.
// ─────────────────────────────────────────────────────────────

/**
 * Injects a <script> tag dynamically (non-blocking).
 * @param {string} src
 * @param {Object} attrs - Extra attributes e.g. { 'data-id': 'GTM-XXXX' }
 */
function loadScript(src, attrs = {}) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

/**
 * Runs a callback once the browser is idle (requestIdleCallback)
 * with a 2-second timeout fallback.
 */
function onIdle(cb) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 200);
  }
}

// ─────────────────────────────────────────────────────────────
// 4. LOAD YOUR THIRD-PARTY SCRIPTS HERE (deferred until idle)
//
//    ↓ Replace GTM-XXXXXX with your actual GTM container ID
//    ↓ Add or remove scripts as needed for your site
// ─────────────────────────────────────────────────────────────

onIdle(async () => {
  // --- Google Tag Manager ---
  // Replace GTM-XXXXXX with your real container ID
  (function(w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', 'GTM-XXXXXX');

  await yieldToMain();

  // --- Google Analytics (if loaded directly, not via GTM) ---
  // Remove this block if you already load GA through GTM
  // await loadScript('https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX');
  // window.dataLayer = window.dataLayer || [];
  // function gtag(){ dataLayer.push(arguments); }
  // gtag('js', new Date());
  // gtag('config', 'G-XXXXXXXXXX');

  await yieldToMain();

  // --- Chat widget (e.g. Tidio, Intercom, Freshchat) ---
  // Only load after user first moves mouse or touches screen
  // to avoid any INP impact on initial page load
  loadChatOnFirstInteraction();
});

// ─────────────────────────────────────────────────────────────
// 5. CHAT WIDGET FACADE
//    Delays chat widget load until the user first interacts.
//    Saves ~200-400ms of main thread blocking on load.
// ─────────────────────────────────────────────────────────────

function loadChatOnFirstInteraction() {
  const events = ['mousemove', 'touchstart', 'keydown', 'scroll'];

  function loadChat() {
    events.forEach(e => window.removeEventListener(e, loadChat));

    // ↓ Replace with your actual chat widget script URL
    // Example for Tidio:
    // loadScript('//code.tidio.co/XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX.js');

    // Example for Crisp:
    // window.$crisp = [];
    // window.CRISP_WEBSITE_ID = 'YOUR-CRISP-ID';
    // loadScript('https://client.crisp.chat/l.js');
  }

  events.forEach(e => window.addEventListener(e, loadChat, { once: true, passive: true }));
}

// ─────────────────────────────────────────────────────────────
// 6. INTERACTION HANDLER WRAPPER
//    Wrap click handlers with this to prevent them from
//    causing long tasks that inflate INP.
//
//    BEFORE:
//      button.addEventListener('click', heavyFunction);
//
//    AFTER:
//      button.addEventListener('click', withYield(heavyFunction));
// ─────────────────────────────────────────────────────────────

function withYield(handler) {
  return async function(event) {
    // Let the browser paint the interaction feedback first
    await yieldToMain();
    // Then run the actual handler
    handler.call(this, event);
  };
}

// ─────────────────────────────────────────────────────────────
// 7. DEBOUNCE — prevents rapid-fire events (scroll, resize,
//    search input) from flooding the main thread.
// ─────────────────────────────────────────────────────────────

function debounce(fn, wait = 150) {
  let timer;
  return function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Example: debounce your search/filter inputs
// const searchInput = document.querySelector('#search');
// if (searchInput) {
//   searchInput.addEventListener('input', debounce(handleSearch, 200));
// }

// ─────────────────────────────────────────────────────────────
// 8. EXPORT for use in other modules (if using a bundler)
// ─────────────────────────────────────────────────────────────

// If you're using a module bundler (Webpack/Vite/Rollup):
// export { yieldToMain, processInChunks, withYield, debounce, loadScript, onIdle };